import React, {useMemo} from "react";
import * as Yup from "yup";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Col, Label, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useFormik} from "formik";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import {ORDER_STATUSES} from "../constants/order";
import Select from "../../../../components/Common/Select";

interface IChangeStatus {
    isOpen: boolean;
    toggle: () => void;
    id: string;
    date: string;
}

const validationSchema = Yup.object({
    status: Yup.number().required("Status must be selected"),
});

interface StatusOrder {
    status: number;
}

const ChangeStatus: React.FC<IChangeStatus> = ({isOpen, toggle, id, date}) => {
    const queryClient = useQueryClient();

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyTrafficOrders"]})
            .then(() => toggle())
    }

    const {data: orderStatus, isFetching: isLoadingStatus} = useQuery({
        queryKey: ["keyStatusTrafficOrder", +id, date],
        queryFn: () => TrafficOrderService.getStatusTrafficOrder(+id, date),
    });

    const {mutate: updateOrderStatus, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: StatusOrder) => TrafficOrderService.updateStatusTrafficOrder(data, +id),
        mutationKey: ["keyUpdateStatusTrafficOrder", id],
        onSuccess: onSuccess
    });

    const initialValues = useMemo(() => ({
        status: orderStatus?.status as number || null,
    }), [orderStatus?.status]);

    const handleSubmitForm = (values: any) => {
        updateOrderStatus(values);
    }

    const {
        values,
        handleSubmit,
        isValid,
        setFieldValue,
        resetForm,
        errors,
        touched
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleChangeStatus = (selected: any) => {
        setFieldValue("status", selected ? selected.value : null)
    }

    const EXCLUDED_STATUSES = [4, 5, 6];
    const statusSelect = ORDER_STATUSES
        .filter(({value}) => !EXCLUDED_STATUSES.includes(value))
        .map(status => ({value: status.value, label: status.label}));

    const selectedValue = useMemo(() =>
            statusSelect?.find((b: any) => b.value === values.status) || null
        , [values.status, statusSelect]);

    const closeModal = () => {
        toggle();
        resetForm();
    };

    const isSaving = isPendingUpdate || !!queryClient.isFetching({queryKey: ["keyTrafficOrders"]});

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>Change Status Order {id}</ModalHeader>
            <ModalBody>
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <div className="mb-4">
                                <Label>Select status: </Label>
                                <Select
                                    isClearable
                                    name="status"
                                    label="Status"
                                    placeholder="Select status..."
                                    isLoading={isLoadingStatus}
                                    isDisabled={isLoadingStatus || isSaving}
                                    options={statusSelect}
                                    value={!isLoadingStatus && selectedValue}
                                    onChange={handleChangeStatus}
                                />

                                {!!(errors.status && touched.status) && (
                                    <div className="invalid-feedback d-block m-0">{errors.status}</div>)}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-end">
                            <button type="submit" className="btn btn-success"
                                    disabled={!isValid || isLoadingStatus || isSaving}>
                                {isSaving ? "Loading..." : "Save"}
                            </button>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default ChangeStatus;